/* @flow */
import React, { Component } from "react";
import styled, { css } from "styled-components";
import { rgba } from "polished";
import formatTime from "./formatTime";
import Gettext from "../common/Gettext";
import Deal from "./Deal";
import { OtcImage } from "../Trade/StockClose";
import type {
  Stock,
  Deal as DealObject,
  Account
} from "../../redux/CommonTypes";
import type { StockPoint } from "../../redux/reducer/points";

import { fontSize } from "../common/styled-mixins";

const Time = styled.div`
  font-size: ${fontSize(12, 12)};
  color: ${props => props.theme.alternativeAccent};
`;

// const ProgressContainer = styled.div`
//   position: absolute;
//   bottom: 0px;
//   left: 0px;
//   width: 100%;
//   height: 4px;
//   background-color: ${props => props.theme.mainLight};
// `;
//
// const Progress = styled.div`
//   height: 4px;
//   width: 0%;
//   background-color: ${props => props.theme.accent};
//   transition: all 0.1s ease;
// `;

const CloseButtonView = styled.button`
  position: absolute;
  top: 7px;
  right: 7px;
  width: 16px;
  height: 16px;
  margin: 0px;
  padding: 0px;
  line-height: 0;
  color: #95989A;
  background-color: transparent;
  border: none;
  cursor: pointer;
  pointer-events: auto;

  :hover {
    color: ${({ theme }) => theme.foreground};
  }
`;

const TabPercentTitle = styled.div`
  text-align: right;
  font-size: ${fontSize(14, 14)};
  font-weight: 700;
  color: ${props => (props.theme.isIntlProjectLight ? "#ffffff" : props.theme.accent)};
`;

const Deals = styled.div`
  display: inline-block;
  height: 17px;
  margin-left: -5px;
  padding-left: 5px;
  padding-right: 5px;
  font-size: ${fontSize(12)};
  line-height: 17px;
  text-transform: lowercase;
  color: ${({ theme }) => theme.lightWhiteForeground};
  background-color: transparent;

  :hover {
    background-color: ${({ theme }) => theme.main};
  }
`;

const TabView = styled.div`
  position: relative;
  display: block;
  height: 100%;
  width: 100%;
  max-width: 174px;
  margin-right: 1px;
  padding: 8px 15px;
  color: ${({ theme }) => rgba(theme.foreground, 0.5)};
  border-right: 1px solid ${props => props.theme.mainSeparator};
  background-color: ${props => props.theme.mainDark};
  background-image: linear-gradient(
    45deg,
    ${props => props.theme.mainDark} 0%,
    ${props => props.theme.main} 100%
  );
  background-repeat: no-repeat;
  background-position: right top;
  cursor: pointer;
  ${({ theme }) =>
    theme.isIntlProjectLight &&
    css`
      color: #ffffff;
      background-color: rgba(0, 0, 0, 0.25);
      background-image: none;
      border: none !important;
      border-radius: 4px 4px 0 0;
      
      ${TabPercentTitle} {
        color: ${({ theme }) => theme.alternativeForeground};
      }
    `};

  :first-of-type {
    border-left: 1px solid ${props => props.theme.mainSeparator};
  }

  :hover {
    background: ${props => props.theme.mainLight};
    ${({ theme }) =>
      theme.isIntlProjectLight &&
      css`
        color: ${({ theme }) => theme.foreground};
        ${TabPercentTitle} {
          color: ${({ theme }) => theme.alternativeForeground};
        }
      `};
    ${Deals} {
      color: ${({ theme }) => theme.alternativeForeground};
    }
  }

  ${props =>
    props.active &&
    css`
      background-color: ${props => props.theme.mainLight};
      background-image: linear-gradient(
        45deg,
        ${props => props.theme.mainLight} 0%,
        ${props => props.theme.main} 100%
      );
      box-shadow: inset 0px -2px 0px 0px ${props => props.theme.accent};
      
      ${({theme}) => theme.isIntlProjectLight && css`
        box-shadow: none;
      `};
      ${TabPercentTitle} {
        color: ${({ theme }) => theme.foreground};
      }
      ${Deals} {
        color: ${({ theme }) => theme.alternativeForeground};
      }
    `};
`;

const TableContainer = styled.div`
  display: table;
  width: 100%;
  height: 100%;
`;

const TableCell = styled.div`
  display: table-cell;
  vertical-align: middle;
`;

const TabTitle = styled.div`
  display: flex;
  align-items: center;
  align-content: space-between;
  justify-content: space-between;
  width: 100%;
  margin: 3px 0;

  ${props =>
    props.active &&
    css`
      color: ${({ theme }) => theme.foreground};
    `};
`;

const VerticalTabs = styled.div`
  position: absolute;
  top: 74px;
  left: -1px;
  z-index: 110;
`;

const Title = styled.div`
  font-size: ${fontSize(14, 14)};
  line-height: 1;
  text-transform: uppercase;
`;

const Disabled = styled.div`
  display: inline-block;
  margin-left: auto;
  opacity: 0.5;
  cursor: default;

  :hover {
    opacity: 1;
  }
`;

function CloseButton(props) {
  return (
    <CloseButtonView {...props}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        version="1.1"
        viewBox="0 0 24 24"
        fill="currentColor"
      >
        <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
      </svg>
    </CloseButtonView>
  );
}

type Props = {
  id: number,
  stock: Stock,
  deals: Array<DealObject>,
  points: Array<StockPoint>,
  time: number,
  isActive: boolean,
  onClick: (id: number) => void,
  onRemoveClick: (event: Event) => void,
  activeAccount: Account
};

type State = {
  isOpen: boolean
};

class Tab extends Component<Props, State> {
  state = {
    isOpen: false
  };

  open = () => {
    this.setState({ isOpen: true });
  };

  close = () => {
    this.setState({ isOpen: false });
  };

  render() {
    if (
      typeof this.props.stock === "undefined" ||
      typeof this.props.activeAccount === "undefined"
    ) {
      return null;
    }

    const hasActiveDeals = this.props.deals.length > 0;
    const currentPoint = Array.isArray(this.props.points)
      ? this.props.points[this.props.points.length - 1]
      : null;

    const endDate = hasActiveDeals ? this.props.deals[0].timestamp * 1000 : 0;
    const endDuration = hasActiveDeals ? this.props.deals[0].bTime * 1000 : 0;

    let endTime = hasActiveDeals
      ? this.props.deals.reduce((value, deal) => {
          const date = deal.timestamp * 1000;
          const duration = deal.bTime * 1000;
          return Math.min(value, date + duration);
        }, endDate + endDuration)
      : 0;

    let profit;
    if (this.props.tradeMode === "tournament") {
      profit = this.props.stock.realProfit - this.props.stock.profit_vip;
    } else {
      profit =
        this.props.activeAccount.type === "real"
          ? this.props.stock.realProfit
          : this.props.stock.demoProfit;
    }

    return (
      <TabView
        onMouseLeave={this.close}
        onClick={this.props.onClick}
        active={this.props.isActive}
        enabled={this.props.stock.enabled}
      >
        <TableContainer>
          <TableCell>
            {hasActiveDeals && (
              <Time>{formatTime(endTime - this.props.time)}</Time>
            )}

            {this.props.shouldShowCloseButton === true && (
              <CloseButton onClick={this.props.onRemoveClick} />
            )}

            <TabTitle active={this.props.isActive}>
              <Title>
                {this.props.stock.stock.replace("_OTC", "")}
                {this.props.stock.stock.endsWith("_OTC") && (
                  <OtcImage tab>OTC</OtcImage>
                )}
              </Title>
              {this.props.stock.enabled ? (
                <TabPercentTitle
                  green={
                    this.props.activeAccount.type === "real" &&
                    this.props.tradeMode === "binary"
                  }
                >
                  {profit}%
                </TabPercentTitle>
              ) : (
                <Disabled>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    height="16"
                    width="16"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M0 0h24v24H0z" fill="none" />
                    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
                  </svg>
                </Disabled>
              )}
            </TabTitle>

            {hasActiveDeals && (
              <Deals onMouseEnter={this.open} key={1}>
                {this.props.deals.length} <Gettext>Deals</Gettext>
              </Deals>
            )
            // ,
            // <ProgressContainer key={2}>
            //   <Progress style={{ width: `${0}%` }} />
            // </ProgressContainer>
            }

            {hasActiveDeals &&
              currentPoint &&
              this.state.isOpen && (
                <VerticalTabs>
                  {this.props.deals.map((deal, i) => (
                    <Deal
                      key={i}
                      {...deal}
                      currentPoint={currentPoint}
                      tradeMode={this.props.tradeMode}
                    />
                  ))}
                </VerticalTabs>
              )}
          </TableCell>
        </TableContainer>
      </TabView>
    );
  }
}

export default Tab;



// WEBPACK FOOTER //
// ./src/components/Tabs/Tab.js